import { BaseUploadData, InPlaceUpload, InPlaceUploadBaseProps } from "./InPlaceUpload";
import { useEffect, useState } from "react";
import { postKmsData } from "@kaltura/mediaspace-shared-utils";
import { useEventHandler } from "@kaltura/mediaspace-shared-hooks";

export interface InPlaceUploadConnectorProps<DataT extends BaseUploadData = BaseUploadData>
    extends InPlaceUploadBaseProps<DataT> {
    /**
     * params to add to all requests to kms(category id, entry id etc.)
     */
    postParams?: Record<string, string>;

    /**
     * kms url to call once the upload is done.
     * postParams and tokenId will be sent to this url.
     */
    createMediaUrl: string;

    /**
     * kms url to call to check on the newly created entry.
     * postParams and the newly created entryId will be sent to this url.
     */
    checkStatusUrl?: string;

    /**
     * kms url to call to remove the current kms saved value.
     * postParams will be sent to this url.
     * if not passed, the "remove" menu item will not show.
     */
    removeUrl?: string;

    /**
     * Called with the full response data after successful creation.
     */
    onCreateDone?: (data: DataT) => void;

    /**
     * Called when the user removes the current value.
     */
    onRemove?: () => void;

    /**
     * Called on any data object change.
     */
    onDataChange?: (data: DataT | undefined) => void;
}

/**
 * connect the in-place upload widget to kms
 *
 *  - send an upload token
 *  - checks on the entry progress (optional)
 *  - receives a thumbnail url
 */
export const InPlaceUploadConnector = <DataT extends BaseUploadData>(props: InPlaceUploadConnectorProps<DataT>) => {
    const {
        postParams,
        createMediaUrl,
        checkStatusUrl,
        removeUrl,
        data: dataProp,
        onCreateDone,
        onRemove,
        onDataChange,
        ...rest
    } = props;

    const [hasError, setHasError] = useState(false);
    const [data, setData] = useState<DataT | undefined>(dataProp);

    const handleDataChange = useEventHandler((newData: DataT | undefined) => {
        // Notify the parent component only on real changes
        if (newData !== data) {
            onDataChange?.(newData);
        }

        setData(newData);
    });

    // if prop value changes, update state.
    // this allows additional actions to update the thumbnail.
    useEffect(() => {
        handleDataChange(dataProp);
    }, [handleDataChange, dataProp]);

    const handleUploadStart = () => {
        // reset previous processing errors
        setHasError(false);
    };

    const handleUploadDone = async (tokenId: string) => {
        // call kms with the upload token to create a new entry
        try {
            const { success, ...data } = await postKmsData(createMediaUrl, { ...postParams, tokenId: tokenId });
            const { entryId } = data;

            // set error
            !success && setHasError(true);

            // set thumbnail if we do not need to check the entry status
            !checkStatusUrl && handleDataChange(data);

            // call create done callback
            success && onCreateDone?.(data);

            // check the newly created entry status
            checkStatusUrl && entryId && checkEntryStatus(entryId, data, checkStatusUrl);
        }
        catch (e) {
            setHasError(true);
        }
    };

    const checkEntryStatus = (entryId: string, data: DataT, checkStatusUrl: string) => {
        // check entry status
        const intervalId = setInterval(
            async () => {
                try {
                    const {
                        success,
                        done,
                        data: newData,
                    } = await postKmsData(checkStatusUrl, { ...postParams, entryId: entryId });

                    if (success && done) {
                        handleDataChange(newData ?? data);
                        clearInterval(intervalId);
                    }

                    if (!success && done) {
                        setHasError(true);
                        clearInterval(intervalId);
                    }
                }
                catch (e) {
                    setHasError(true);
                    clearInterval(intervalId);
                }
            },
            7000
        );
    };

    const handleRemove = async () => {
        if (onRemove) {
            onRemove();
            // remove current value
            handleDataChange(undefined);
        }

        if (!removeUrl) {
            // do nothing.
            return;
        }

        try {
            const { success } = await postKmsData(removeUrl, { ...postParams });

            // set error
            !success && setHasError(true);
        }
        catch (e) {
            setHasError(true);
        }

        // remove current value
        handleDataChange(undefined);
    };

    return (
        <InPlaceUpload
            {...rest}
            onUploadStart={handleUploadStart}
            onUploadDone={handleUploadDone}
            onRemove={removeUrl || onRemove ? handleRemove : undefined}
            hasError={hasError}
            data={data}
        />
    );
};
